import React from 'react'
import FormControl from '~/components/FormControl'
import HelperText from '~/components/HelperText'
import LabelText from '~/components/LabelText'
import type { BaseTextFieldProps } from '~/components/TextField'

export type SelectProps = BaseTextFieldProps &
  React.ComponentPropsWithoutRef<'select'> & {
    placeholder?: string
  }

const Select = React.forwardRef(
  (
    {
      className,
      helperText,
      fullWidth,
      isError,
      htmlFor,
      label,
      ...restSelectProps
    }: SelectProps,
    ref: React.ForwardedRef<HTMLSelectElement>
  ) => {
    const ariaDescribedByFallback = `${React.useId()}-helper`
    const ariaDescribedBy = helperText
      ? restSelectProps['aria-describedby'] ||
        (restSelectProps.name ? `${restSelectProps.name}-helper` : '') ||
        ariaDescribedByFallback
      : ''

    return (
      <FormControl
        fullWidth={fullWidth}
        className={className}
        disabled={restSelectProps.disabled}
      >
        <label htmlFor={htmlFor} className="group">
          <div className="flex justify-start">
            <LabelText isError={isError} isRequired={restSelectProps.required}>
              {label}
            </LabelText>
          </div>
          <div className="border-b border-black/40 py-4 pl-0 pr-6 group-focus-within:border-black group-hover:border-black/80">
            <select
              className="text-body w-full bg-white text-input text-black/30 outline-none valid:text-black"
              aria-invalid={isError}
              id={htmlFor}
              {...restSelectProps}
              aria-describedby={ariaDescribedBy}
              ref={ref}
            >
              {restSelectProps.placeholder ? (
                <option disabled value="">
                  {restSelectProps.placeholder}
                </option>
              ) : null}
              {restSelectProps.children}
            </select>
          </div>
        </label>
        {helperText ? (
          <HelperText id={ariaDescribedBy} isError={isError}>
            {helperText}
          </HelperText>
        ) : null}
      </FormControl>
    )
  }
)

export default Select
